import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParceiro } from "./hooks/useParceiro";
import { useAuth } from "resources/AuthProvider";

import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  IconButton,
} from "@mui/material";

import { 
  Edit, 
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined, 
  KeyboardDoubleArrowRightOutlined,
  ToggleOffOutlined,
  ToggleOnOutlined,
  CheckOutlined,
  CancelOutlined,
  DomainAddOutlined,
} from "@mui/icons-material";

import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import SelectField from "../../atoms/SelectField/SelectField";
import CnpjField from "../../atoms/CnpjField/CnpjField";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import MultiActionButton from "../../atoms/MultiActionButton/MultiActionButton";

import { Parceiro } from "../../../types/Parceiros";
import { formatarCnpj } from "../../../utils/Masks";

/* Styles */
import styles from './GestaoParceiros.module.css';
import styles_filter from "../../../styles/Filter.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";
import styles_button from "../../../styles/Button.module.css";

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

const GestaoParceiros = () => {

  const navigate = useNavigate();
  const { hasActionPermission } = useAuth();
  const canCreateInstituicao = hasActionPermission("instituicoes.create");
  const canUpdateInstituicao =
    hasActionPermission("instituicoes.update_all") ||
    hasActionPermission("instituicoes.update_own");
  const canDeleteInstituicao =
    hasActionPermission("instituicoes.delete_all") ||
    hasActionPermission("instituicoes.delete_own");
  const showActionsColumn = canUpdateInstituicao || canDeleteInstituicao;

  const { 
    msgSuccess,
    msgError,
    loading,
    parceiros,
    currentPage,
    perPage,
    totalPages,
    openExportPdf,
    setOpenExportPdf,
    openExportXls,
    setOpenExportXls,
    loadingExport,
    searchNome,
    setSearchNome,
    searchNomeResp,
    setSearchNomeResp,
    searchCnpj,
    setSearchCnpj,
    searchEmail,
    setSearchEmail,
    searchStatus,
    setSearchStatus,
    setMsgError,
    setMsgSuccess,
    setCurrentPage,
    setPerPage,
    inactivatePartner,
    activatePartner,
    recarregarParceiros,
    inactivateMultipleInstitutions,
    activateMultipleInstitutions,
    filterParceiros,
    hesetFilter,
    handleExportPdf,
    handleExportXls,
  } = useParceiro();

  const [selectedPartners, setSelectedPartners] = useState<Set<string>>(new Set());

  const [loadingConfirmDialog, setLoadingConfirmDialog] = useState<boolean>(false);
  const [openInactivate, setOpenInactivate] = useState<boolean>(false);
  const [openActivate, setOpenActivate] = useState<boolean>(false);
  const [openMultipleInactivate, setOpenMultipleInactivate] = useState<boolean>(false);
  const [openMultipleActivate, setOpenMultipleActivate] = useState<boolean>(false);
  const [selectedPartnerForEdit, setSelectedPartnerForEdit] = useState<Parceiro | null>(null);

  const statusOptions = [
    { id: 1, nome: "Ativo" },
    { id: 2, nome: "Inativo" },
  ];

  // FunÃ§Ã£o para lidar com a seleÃ§Ã£o de procedimentos para fechamento
  const handlePartnerSelection = (id: string | number) => {
    const procedureId: string = String(id);

    setSelectedPartners(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(procedureId)) {
        newSelected.delete(procedureId);
      } else {
        newSelected.add(procedureId);
      }
      return newSelected;
    });
  };

  const handleEditPartner = (id :number) => {
    navigate(`/partner?id=${id}`);
  };

  const handleNewPartner = () => {
    navigate(`/partner`);
  };

  const handleOpenInactivatePartner = (parceiro: Parceiro) => {
    setSelectedPartnerForEdit(parceiro);
    setOpenInactivate(true);
  };

  const handleOpenActivatePartner = (parceiro: Parceiro) => {
    setSelectedPartnerForEdit(parceiro);
    setOpenActivate(true);
  };

  const handleInactivatePartner = async () => {
    try {
      if (!selectedPartnerForEdit) {
        return;
      }
      setLoadingConfirmDialog(true);
      await inactivatePartner(selectedPartnerForEdit.id);
      setLoadingConfirmDialog(false);
      setOpenInactivate(false);
      recarregarParceiros();
      setMsgSuccess('Instituição inativada com sucesso!');
    } catch (error) {
      setLoadingConfirmDialog(true);
      setOpenInactivate(false);
      setMsgError('não foi possível inativar a Instituição!');
    }
  };

  const handleActivatePartner = async () => {
     try {
      if (!selectedPartnerForEdit) {
        return;
      }
      setLoadingConfirmDialog(true);
      await activatePartner(selectedPartnerForEdit.id);
      setLoadingConfirmDialog(false);
      setOpenActivate(false);
      recarregarParceiros();
      setMsgSuccess('Instituição reativada com sucesso!');
    } catch (error) {
      setLoadingConfirmDialog(true);
      setOpenActivate(false);
      setMsgError('não foi possível reativar a Instituição!');
    }
  };

  const handleInactivateMultiplePartners = async () => {
    try {
      setLoadingConfirmDialog(true);
      const stringArray = Array.from(selectedPartners);
      const numberArray = stringArray.map(item => Number(item));
      
      await inactivateMultipleInstitutions(numberArray);
      setMsgSuccess('Instituições inativadas com sucesso!');

      setLoadingConfirmDialog(false);
      recarregarParceiros();
      setOpenMultipleInactivate(false);
      setSelectedPartners(new Set());
      
    } catch (error) {
      setLoadingConfirmDialog(false);
      setOpenMultipleInactivate(false);
      setMsgError('não foi possível inativar as Instituições!');
    }
  };

  const handleActivateMultiplePartners = async () => {
    try {
      setLoadingConfirmDialog(true);
      const stringArray = Array.from(selectedPartners);
      const numberArray = stringArray.map(item => Number(item));
      
      await activateMultipleInstitutions(numberArray);
      setMsgSuccess('Instituições reativadas com sucesso!');

      setLoadingConfirmDialog(false);
      recarregarParceiros();
      setOpenMultipleActivate(false);
      setSelectedPartners(new Set());
      
    } catch (error) {
      setLoadingConfirmDialog(false);
      setOpenMultipleActivate(false);
      setMsgError('não foi possível reativar as Instituições!');
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>
          Gestão de Parceiros - Instituições
        </div>
        <div className={styles_filter.container}>
          <div className={styles_filter.grid12}>
            <div className={styles_filter.inputGroup4}>
              <label className={styles_input.titleInput}>
                Nome da Instituição
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                data-testid="nome"
                value={searchNome}
                onChange={(e) => setSearchNome(e.target.value)}
                required
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  className: styles_input.input,
                }}
                sx={{
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                }}
              />
            </div>

            <div className={styles_filter.inputGroup4}>
              <label className={styles_input.titleInput}>
                Responsável Legal
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                data-testid="nome"
                value={searchNomeResp}
                onChange={(e) => setSearchNomeResp(e.target.value)}
                required
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  className: styles_input.input,
                }}
                sx={{
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                }}
              />
            </div>

            <div className={styles_filter.inputGroup4}>
              <label className={styles_input.titleInput}>
                CNPJ
              </label>
              <CnpjField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                value={searchCnpj}
                onUpdate={(value) => setSearchCnpj(value)}
                customClassName={styles_input.input}
              />
            </div>

            <div className={styles_filter.inputGroup4}>
              <label className={styles_input.titleInput}>
                E-mail
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                data-testid="email"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                required
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  className: styles_input.input,
                }}
                sx={{
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:after': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                }}
              />
            </div>

            <div className={styles_filter.inputGroup3}>
              <label className={styles_input.titleInput}>
                Status
              </label>
              <SearchSelect
                label=""
                key="status_filter"
                options={statusOptions}
                value={searchStatus ?? null}
                onChange={(id) => {setSearchStatus(id ?? undefined);}}
                paperClassname={styles_input.paper}
                popperClassname={styles_input.popper}
                labelClassname={styles_input.labelSelect}
                inputClassname={styles_input.inputSelect}
              />
            </div>

            <div className={styles_filter.inputGroup5}>
              <div className={styles_filter.containerButton}>
                <div className={styles_filter.lineButton}>
                  {/* Limpar */}
                  <Button 
                    variant="contained"
                    onClick={hesetFilter} 
                    className={styles_filter.buttonClean}
                    color="primary"
                  >
                    Limpar
                  </Button>
                  
                  {/* Pesquisar */}
                  <Button
                    variant="contained"
                    onClick={filterParceiros}
                    className={styles_filter.buttonSearch}
                    color="primary"
                  >
                    Pesquisar
                  </Button>
                </div>

                <div className={styles_filter.lineButton}>
                  <Button
                    variant="contained"
                    className={styles_filter.buttonXls}
                    color="primary"
                    onClick={() => setOpenExportXls(true)}
                  >
                    Gerar XLS
                  </Button>

                  <Button
                    variant="contained"
                    className={styles_filter.buttonPdf}
                    color="primary"
                    onClick={() => setOpenExportPdf(true)}
                  >
                    Gerar PDF
                  </Button>
                </div>
              </div>
            </div>

          </div>
        </div>
        
        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">
              {msgSuccess}
            </Alert>
          </div>
        )}
        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">
              {msgError}
            </Alert>
          </div>
        )}

        <div className={styles_table.actionsMass}>
          {canCreateInstituicao && (
            <Button
              className={styles_button.buttonPrimary}
              variant="outlined"
              startIcon={<DomainAddOutlined />}
              onClick={handleNewPartner}
            >
              Cadastrar Instituição
            </Button>
          )}

          {canDeleteInstituicao && selectedPartners.size > 0 && (
            <MultiActionButton
              buttonLabel="Ações em Massa"
              actions={[
                {
                  label: "Inativar Instituições",
                  icon: <CancelOutlined />,
                  onClick: () => setOpenMultipleInactivate(true),
                },
                {
                  label: "Reativar Instituições",
                  icon: <CheckOutlined />,
                  onClick: () => setOpenMultipleActivate(true),
                },
              ]}
            />
          )}
        </div>

        <TableContainer className={styles_table.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={styles_table.tableHead}>
                <TableRow>
                  {canDeleteInstituicao && <TableCell padding="none" className={styles_table.tableHeadRow}></TableCell>}
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">Instituição</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="left">CNPJ</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Responsável Legal</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>E-mail</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Telefone</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Tipo de Instituição</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Status</TableCell>
                  {showActionsColumn && <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {parceiros.map((parceiro, index) => (
                  <TableRow
                    key={parceiro.id}
                    className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                  >
                    {canDeleteInstituicao && (
                      <TableCell padding="none">
                        <Checkbox {...label}
                          checked={selectedPartners.has(String(parceiro.id))}
                          onChange={() => handlePartnerSelection(parceiro.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell padding="none" className={styles_table.textXs} align="left">{parceiro.nome}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>
                      {formatarCnpj(parceiro.cnpj)}
                    </TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{parceiro.resp_nome}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{parceiro.email}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{parceiro.telefone}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{parceiro.institution_type?.nome || parceiro.tipo || ""}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs} align="center">{parceiro.ativo ? "Ativo" : "Inativo"}</TableCell>
                    {showActionsColumn && (
                      <TableCell padding="none">
                        <div className={styles_table.blockActions}>
                          {canUpdateInstituicao && (
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Editar Parceiro"
                              disabled={selectedPartners.size > 0 || !parceiro.ativo ? true : false}
                              onClick={() => handleEditPartner(parceiro.id)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          )}

                          {canDeleteInstituicao && parceiro.ativo ? (
                          
                          <IconButton 
                            size="small"
                            color="primary" 
                            aria-label="Inativar Parceiro" 
                            disabled={selectedPartners.size > 0 ? true : false}
                            onClick={() => handleOpenInactivatePartner(parceiro)}
                          >
                            <ToggleOnOutlined fontSize="small" />
                          </IconButton>

                          ) : canDeleteInstituicao ? (

                          <IconButton 
                            size="small"
                            color="primary" 
                            aria-label="Ativar Usuário" 
                            disabled={selectedPartners.size > 0 ? true : false}
                            onClick={() => handleOpenActivatePartner(parceiro)}
                          >
                            <ToggleOffOutlined fontSize="small" />
                          </IconButton>

                          ) : null}

                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <ConfirmDialog
            open={openInactivate}
            loading={loadingConfirmDialog}
            onClose={() => setOpenInactivate(false)}
            onConfirm={() => handleInactivatePartner()}
            title="Inativar Instituição!"
            message={`Deseja realmente inativar o parceiro "${selectedPartnerForEdit?.nome}"?`}
            titleLoadButton="Inativando..."
            titleButtonConfirm="Inativar"
          />

          <ConfirmDialog
            open={openActivate}
            loading={loadingConfirmDialog}
            onClose={() => setOpenActivate(false)}
            onConfirm={() => handleActivatePartner()}
            title="Reativar Instituição!"
            message={`Deseja realmente reativar o parceiro "${selectedPartnerForEdit?.nome}"?`}
            titleLoadButton="Reativando..."
            titleButtonConfirm="Reativar"
          />

          <ConfirmDialog
            open={openMultipleInactivate}
            loading={loadingConfirmDialog}
            onClose={() => setOpenMultipleInactivate(false)}
            onConfirm={() => handleInactivateMultiplePartners()}
            title="Inativar Instituições"
            message={`Deseja realmente inativar as Instituições selecionadas?`}
            titleLoadButton="Inativando..."
            titleButtonConfirm="Inativar"
          />

          <ConfirmDialog
            open={openMultipleActivate}
            loading={loadingConfirmDialog}
            onClose={() => setOpenMultipleActivate(false)}
            onConfirm={() => handleActivateMultiplePartners()}
            title="Reativar Instituições"
            message={`Deseja realmente reativar as Instituições selecionadas?`}
            titleLoadButton="Inativando..."
            titleButtonConfirm="Inativar"
          />


          <ConfirmDialog
            open={openExportPdf}
            loading={loadingExport}
            onClose={() => setOpenExportPdf(false)}
            onConfirm={() => handleExportPdf()}
            title="Exportar parceiros em PDF"
            message={`Deseja confirmar a exportação dos parceiros em PDF?`}
            titleLoadButton="Exportando..."
            titleButtonConfirm="Exportar"
          />

          <ConfirmDialog
            open={openExportXls}
            loading={loadingExport}
            onClose={() => setOpenExportXls(false)}
            onConfirm={() => handleExportXls()}
            title="Exportar parceiros em XLS"
            message={`Deseja confirmar a exportação dos parceiros em XLS?`}
            titleLoadButton="Exportando..."
            titleButtonConfirm="Exportar"
          />
        </TableContainer>
      </CardContent>

      {/* Inicio Bloco Paginação */}
      <CardActions className={styles_table.containerPagination}>
        <div className={styles_table.qtdItems}>
          <div className={styles_table.labelSelect}>
            Resultados por página
          </div>
          <SelectField
            label=""
            value={perPage.toString() || ""}
            onChange={(value) => setPerPage(Number(value))}
            options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
            disableClearable
            disableTyping
          />
        </div>
        <div className={styles_table.pagination}>

          <IconButton 
            className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage} 
            aria-label="Primeira página" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            <KeyboardDoubleArrowLeftOutlined />
          </IconButton>

          <IconButton 
            className={currentPage === 1 ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
            aria-label="página Anterior" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <KeyboardArrowLeftOutlined />
          </IconButton>
          <span className={styles_table.pages}>
            página {currentPage} de {totalPages}
          </span>
    
          <IconButton 
            className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
            aria-label="PrÃ³xima página" 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            <KeyboardArrowRightOutlined />
          </IconButton>

          <IconButton 
            className={currentPage === totalPages ? styles_table.iconButtonPageDisabled : styles_table.iconButtonPage}
            aria-label="Primeira página" 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            <KeyboardDoubleArrowRightOutlined />
          </IconButton>
  
        </div>

        <div className={styles_table.setNumberPage}>
          <div className={styles_table.labelSelect}>
            Ir para a página
          </div>
          <SelectField
            label=""
            value={currentPage.toString() || ""}
            onChange={(value) => setCurrentPage(Number(value))}
            options={Array.from({ length: totalPages }, (_, index) => ({
              nome: String(index + 1) 
            }))}
            disableTyping
          />
        </div>

      </CardActions>
      {/* Fim Bloco Paginação */}

    </Card>
  );
};

export default GestaoParceiros;

